import React, { useState, useEffect } from "react";
import { HiOutlinePencil, HiOutlineTrash, HiDownload } from "react-icons/hi";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { buildApiUrl } from "../../../config";

const EditPurchaseOrderModal = ({ referenceNo, onClose }) => {
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(
          buildApiUrl(`/api/purchase-order/purchase-order/${referenceNo}`),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        if (data.data) {
          setPurchaseOrder(data.data);
          setItems(data.data.items);
        } else {
          toast.error("Failed to fetch purchase order data.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } catch (error) {
        toast.error("An error occurred while fetching the purchase order.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrder();
  }, [referenceNo]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl(`/api/purchase-order/purchase-order/${referenceNo}/items`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items }),
        }
      );
      if (response.ok) {
        toast.success("Purchase order updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        onClose();
      } else {
        toast.error("Failed to update purchase order.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("An error occurred while updating the purchase order.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gray-900/70 flex items-center justify-center p-2 sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 10 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden"
        >
          <div className="px-3 py-2 bg-gradient-to-r ">
            <div className="flex justify-between items-center text-gray-700">
              <h2 className="text-lg font-semibold ">Edit Purchase Order</h2>
              <button
                onClick={onClose}
                className=" hover:text-gray-200 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="max-h-[75vh] overflow-y-auto px-3 pb-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
              </div>
            ) : purchaseOrder ? (
              <div className="space-y-4">
                {/* Purchase Order Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      To Person
                    </label>
                    <input
                      type="text"
                      value={purchaseOrder.toPerson}
                      className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-500 cursor-not-allowed focus:ring-0"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Warehouse Name
                    </label>
                    <input
                      type="text"
                      value={purchaseOrder.warehouseName}
                      className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-500 cursor-not-allowed focus:ring-0"
                      readOnly
                    />
                  </div>
                </div>

                {/* Items List */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    Items
                  </h3>
                  <div className="space-y-2">
                    <div className="text-xs bg-gray-200 py-1 rounded-md text-gray-700 font-medium hidden sm:grid grid-cols-4 gap-2">
                      <span className="pl-2">Item Code</span>
                      <span>Category</span>
                      <span>Item Description</span>
                      <span>Order Qty Ctn.</span>
                    </div>
                    {items.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center bg-gray-50 rounded-md p-2 sm:p-0 transition-colors duration-200"
                      >
                        <div>
                          <label className="block sm:hidden text-xs font-medium text-gray-600">
                            Item Code
                          </label>
                          <input
                            type="text"
                            value={item.itemCode}
                            className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-500 cursor-not-allowed focus:ring-0"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block sm:hidden text-xs font-medium text-gray-600">
                            Category
                          </label>
                          <input
                            type="text"
                            value={item.Category}
                            className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-500 cursor-not-allowed focus:ring-0"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block sm:hidden text-xs font-medium text-gray-600">
                            Item Description
                          </label>
                          <input
                            type="text"
                            value={item.itemDescription}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "itemDescription",
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                          />
                        </div>
                        <div>
                          <label className="block sm:hidden text-xs font-medium text-gray-600">
                            Order Qty Ctn.
                          </label>
                          <input
                            type="number"
                            value={item.orderContainer}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "orderContainer",
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSave}
                    className="px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md text-xs font-medium hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
                  >
                    Save Changes
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-gray-500 text-xs">
                  No data available for this purchase order.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default EditPurchaseOrderModal;
